/* eslint-disable @typescript-eslint/ban-ts-comment */
import { Html, Line } from '@react-three/drei';
import { extend, ThreeEvent, useFrame } from '@react-three/fiber';
import { MeasureKind, UnitsByMeasure } from 'convert';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';
import * as THREE from 'three';
import { StandardWallProps } from '../../@types/Components';
import { ISelectedDoor, ISelectedPassage, ISelectedWindow } from '../../@types/ContextProviderRelated';
import { EActiveDrawer, EDirection, EDraggablePointType, EInteractionMode, ESubMenu, EUnitType, EWallMode } from '../../@types/Enums';
import { Side } from '../../@types/Side';
import '../../App.css';
import { IconDoubleArrow, IconSingleArrowLeft, IconSingleArrowRight } from '../../assets/icons';
import useLongPress from '../../hooks/useLongPress';
import { EHistoryAction } from '../../hooks/useUndoRedo';
import useWallDragging from '../../hooks/useWallDragging';
import DoorModel from '../../models/standard/DoorModel';
import PassageModel from '../../models/standard/PassageModel';
import PhysicalBoundaryModel from '../../models/standard/PhysicalBoundary';
import RadiatorModel from '../../models/standard/RadiatorModel';
import RoomModel from '../../models/standard/RoomModel';
import StandardWallModel from '../../models/standard/StandardWallModel';
import WindowModel from '../../models/standard/WindowModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useDraggingContext } from '../../provider/DraggingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { usePlan } from '../../provider/PlanProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { useZoomChangeContext } from '../../provider/ZoomChangeProvider';
import { fetchAttributesForObject } from '../../utils/attributes/attributesForObjectLoader';
import { DefaultAttributeId, ObjectType } from '../../utils/attributes/defaultObjectsAndAttributes';
import { immersightBlue } from '../../utils/colors';
import {
  DEFAULT_ZOOM_VALUE,
  DEFAULT_RADIATOR_DEPTH,
  DEFAULT_RADIATOR_LENGTH,
  DEFAULT_WALL_OVERLAY_DISTANCE,
  INTERNAL_TO_CM_FACTOR,
  LONG_PRESS_TIME_WALLS,
  UNIT_TO_METER_DIVISOR
} from '../../utils/constants';
import { convertTo } from '../../utils/conversionUtils';
import { isWallShared } from '../../utils/multiroomUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { calculateWallShape, createWallShape, distanceToWallNormal, getAngleFromPoints } from '../../utils/vectorUtils';
import ImsIcon from '../common/ImsIcon';
import Door, { snapDoorToWall } from './Door';
import DraggablePoint from './DraggablePoint';
import OuterWallLengthIndicator from './indicators/OuterWallLengthIndicator';
import WallLengthIndicator from './indicators/WallLengthIndicator';
import Radiator, { snapRadiatorToWall } from './Radiator';
import TempRadiator from './TempRadiator';
import Passage, { snapPassageToWall } from './Passage';
import Window, { snapWindowToWall } from './Window';
extend({ ImsIcon, ReactSVG });

// Memoize the Window & Door & Radiator component for performance optimization.
const MemoizedWindow = memo(Window);
const MemoizedDoor = memo(Door);
const MemoizedPassage = memo(Passage);
const MemoizedRadiator = memo(Radiator);

interface WallLengthArrowOverlayProps {
  wallModel: StandardWallModel;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

const WallLengthArrowOverlay = ({ wallModel, icon }: WallLengthArrowOverlayProps): React.JSX.Element => {
  const { staggeredZoomData } = useZoomChangeContext();
  const iconWrapperRef = useRef<HTMLSpanElement | null>(null);
  const latestTransformRef = useRef<string>('');
  const startX = wallModel.startPoint.x;
  const startY = wallModel.startPoint.y;
  const startZ = wallModel.startPoint.z;
  const endX = wallModel.endPoint.x;
  const endY = wallModel.endPoint.y;
  const endZ = wallModel.endPoint.z;
  const wallDepth = wallModel.depth;

  const overlayPositionArr = useMemo<[number, number, number]>(() => {
    const mid = new THREE.Vector3((startX + endX) * 0.5, (startY + endY) * 0.5, (startZ + endZ) * 0.5);
    const vec = new THREE.Vector3(endX - startX, endY - startY, endZ - startZ);
    const normal = new THREE.Vector3(-vec.y, vec.x, 0).normalize();
    const zoom = staggeredZoomData || DEFAULT_ZOOM_VALUE;
    const constantGap = DEFAULT_WALL_OVERLAY_DISTANCE * (18 / zoom);
    const totalOffsetFromCenter = wallDepth + constantGap;
    const pos = mid.add(normal.multiplyScalar(totalOffsetFromCenter));
    return [pos.x, pos.y, pos.z];
  }, [startX, startY, startZ, endX, endY, endZ, staggeredZoomData, wallDepth]);
  const overlayAngle = useMemo(() => {
    const angle = (-Math.atan2(endY - startY, endX - startX) * 180) / Math.PI;

    return angle < 0 ? angle + 360 : angle;
  }, [startX, startY, endX, endY]);

  useFrame(() => {
    if (!iconWrapperRef.current) return;

    const dx = wallModel.endPoint.x - wallModel.startPoint.x;
    const dy = wallModel.endPoint.y - wallModel.startPoint.y;
    const angle = (-Math.atan2(dy, dx) * 180) / Math.PI;
    const normalizedAngle = angle < 0 ? angle + 360 : angle;
    const transform = `translate(-50%, -50%) scale(3) rotate(${normalizedAngle}deg)`;

    if (latestTransformRef.current === transform) return;

    latestTransformRef.current = transform;
    iconWrapperRef.current.style.transform = transform;
  });

  return (
    <Html name={'wallDirectionIndicatorHtml'} position={overlayPositionArr} style={{ width: 'auto', height: 'auto' }}>
      <span
        ref={iconWrapperRef}
        style={{
          display: 'inline-flex',
          userSelect: 'none',
          pointerEvents: 'none',
          color: immersightBlue,
          stroke: immersightBlue,
          fill: immersightBlue,
          transform: `translate(-50%, -50%) scale(3) rotate(${overlayAngle}deg)`,
          transformOrigin: 'center',
        }}
      >
        <ImsIcon Icon={icon} />
      </span>
    </Html>
  );
};

// Update interface to include roomModel
interface ExtendedStandardWallProps extends StandardWallProps {
  roomModel?: RoomModel;
}

// Wall component: Represents a wall with draggable points and embedded windows.
const StandardWall = ({
  index,
  selectedWall,
  selectedWindow,
  selectedDoor,
  selectedPassage,
  selectedRadiator,
  isPreview,
  onClick,
  onWindowClick,
  onDoorClick,
  onPassageClick,
  onWallUpdate,
  onRadiatorClick,
  camera,
  raycaster,
  planeRef,
  roomModel,
}: ExtendedStandardWallProps): React.JSX.Element => {
  // State for hover and drag actions.
  const [hovered, setHovered] = useState<boolean>(false);
  const { isDragging } = useDraggingContext();
  const { activeDrawer, wallChangeDirection, wallThicknessDirection } = useSelection();
  const { unitType } = useUnitContext();
  const { wallMode, interactionMode, setCurrentlyActiveSubMenu } = useToolbarContext();
  const { showWallInnerLength, showWallOuterLength, showElementDistances } = useMultiSelectToggleContext();
  const { building, updateDoorAttributes, updatePassageAttributes, updateRadiatorAttributes, updateWindowAttributes, getGermanDoorDirectionText, setWallGeometryAndTypeAttributes } = useBuilding();
  const wallModel = building.walls[index] as StandardWallModel;
  const [overlayVisibility, setOverlayVisibility] = useState<boolean>(false);

  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const windowsDependencies = useMemo(
    () => JSON.stringify((wallModel.windows || []).map((window) => ({ position: window.position.toArray(), width: window.length }))),
    [wallModel.windows],
  );
  const doorsDependencies = useMemo(() => JSON.stringify((wallModel.doors || []).map((door) => ({ position: door.position.toArray(), width: door.length }))), [wallModel.doors]);
  const passagesDependencies = useMemo(
    () => JSON.stringify((wallModel.passages || []).map((passage) => ({ position: passage.position.toArray(), width: passage.length }))),
    [wallModel.passages],
  );
  const hasWindowsDoorsOrPassages = useMemo(
    () => (wallModel.windows?.length ?? 0) > 0 || (wallModel.doors?.length ?? 0) > 0 || (wallModel.passages?.length ?? 0) > 0,
    [wallModel.windows?.length, wallModel.doors?.length, wallModel.passages?.length],
  );
  const { wallPerpendicularDrag, setWallPerpendicularDrag, setDraggingPosition } = useWallDragging(raycaster, camera, planeRef, onWallUpdate, wallModel, building.walls);

  const { pointC, pointD, pointC2, pointD2 } = calculateWallShape(wallModel.startPoint, wallModel.endPoint, wallModel.depth, building.walls, index);
  wallModel.setOuterPoints(pointC, pointD);
  wallModel.setOuterPoints2(pointC2, pointD2);

  const shape = useMemo(
    () => createWallShape(wallModel.startPoint, wallModel.endPoint, wallModel.outerStartPoint, wallModel.outerEndPoint, wallModel.outerStartPoint2, wallModel.outerEndPoint2),
    [wallModel, building.walls, index],
  );

  const meshColor = useMemo(() => (selectedWall.wallIndex === index ? '#009fe3' : hovered ? '#565656' : '#111111'), [hovered, selectedWall, index]);
  // 1. Shader NUR EINMAL kompilieren (Leeres Dependency-Array!)
  const hatchMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      extensions: { derivatives: true },
      uniforms: {
        uBaseColor: { value: new THREE.Color('#ffffff') },
        // Startfarbe ist egal, wird durch useEffect überschrieben
        uHatchColor: { value: new THREE.Color('#111111') },
        uSpacing: { value: 1.2 },
        uThickness: { value: 0.2 },
      },
      vertexShader: `
        varying vec2 vWorldPos;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPosition.xy;
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: `
      varying vec2 vWorldPos;
      uniform vec3 uBaseColor;
      uniform vec3 uHatchColor;
      uniform float uSpacing;
      uniform float uThickness;

      void main() {
        float coord = vWorldPos.x - vWorldPos.y;
        float halfSpacing = uSpacing / 2.0;
        float dist = abs(mod(coord + halfSpacing, uSpacing) - halfSpacing);
        float halfThick = uThickness / 2.0;
        
        float f = length(vec2(dFdx(coord), dFdy(coord)));
        float edge = smoothstep(halfThick - f, halfThick + f, dist);
        vec3 lineColor = mix(uHatchColor, uBaseColor, edge);
        
        float averageCoverage = uThickness / uSpacing;
        vec3 averageColor = mix(uBaseColor, uHatchColor, averageCoverage);
        float fade = smoothstep(uSpacing * 0.15, uSpacing * 0.6, f);
        
        gl_FragColor = vec4(mix(lineColor, averageColor, fade), 1.0);
      }
    `,
      side: THREE.DoubleSide,
    });
  }, []); // <--- WICHTIG: LEERES ARRAY!

  useEffect(() => {
    const hatchColor = hatchMaterial.uniforms.uHatchColor.value as THREE.Color;
    hatchColor.set(meshColor);
    hatchColor.convertLinearToSRGB();
  }, [hatchMaterial, meshColor]);

  useEffect(() => () => {
    hatchMaterial.dispose();
  }, [hatchMaterial]);

  const onLongPress = () => {
    setWallPerpendicularDrag(true);
  };
  const { triggerLongPressManually, stopLongPressManually } = useLongPress(onLongPress, LONG_PRESS_TIME_WALLS);
  const unit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  useEffect(() => {
    if (!isDragging) {
      if (wallModel.attributes?.some((attr) => attr.id === DefaultAttributeId.WALL_THICKNESS || attr.id === DefaultAttributeId.WALL_LENGTH)) {
        setWallGeometryAndTypeAttributes(index, unitType);
      }
    }
  }, [wallModel.startPoint, wallModel.endPoint, wallModel.depth, index, unitType, setWallGeometryAndTypeAttributes, isDragging]);

  const handleWallMeshClick = (e: ThreeEvent<PointerEvent>): void => {
    if (wallMode === EWallMode.WINDOW) {
      const windowModel = new WindowModel(snapWindowToWall(wallModel, e.point), undefined, wallModel.depth, getAngleFromPoints(wallModel.startPoint, wallModel.endPoint));
      (wallModel.windows || (wallModel.windows = [])).push(windowModel);
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.WINDOW_ADDED);
      fetchAttributesForObject(windowModel, ObjectType.Window, regionCode).then((attrs) => {
        const widthCm = windowModel.length * INTERNAL_TO_CM_FACTOR;
        const widthValue =
          unitType === EUnitType.METRIC
            ? parseFloat(widthCm.toFixed(3))
            : Math.round(convertTo(widthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
        const widthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.WINDOW_WIDTH);
        if (widthAttr) {
          widthAttr.value = widthValue;
          widthAttr.unit = unit;
        }
        updateWindowAttributes(index, wallModel.windows.length - 1, attrs);
      });
    } else if (wallMode === EWallMode.DOOR) {
      const doorModel = new DoorModel(snapDoorToWall(wallModel, e.point), undefined, wallModel.depth, getAngleFromPoints(wallModel.startPoint, wallModel.endPoint));
      (wallModel.doors || (wallModel.doors = [])).push(doorModel);
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.DOOR_ADDED);
      fetchAttributesForObject(doorModel, ObjectType.Door, regionCode).then((attrs) => {
        const widthCm = doorModel.length * INTERNAL_TO_CM_FACTOR;
        const widthValue =
          unitType === EUnitType.METRIC
            ? parseFloat(widthCm.toFixed(3))
            : Math.round(convertTo(widthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
        const widthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.DOOR_WIDTH);
        if (widthAttr) {
          widthAttr.value = widthValue;
          widthAttr.unit = unit;
        }
        const openingDirAttr = attrs.find((attr) => attr.id === DefaultAttributeId.DOOR_OPENING_DIRECTION);
        if (openingDirAttr) {
          openingDirAttr.value = getGermanDoorDirectionText(doorModel.openingDirection, doorModel.orientation);
        }
        updateDoorAttributes(index, wallModel.doors.length - 1, attrs);
      });
    } else if (wallMode === EWallMode.PASSAGE) {
      const passageModel = new PassageModel(snapPassageToWall(wallModel, e.point), undefined, wallModel.depth, getAngleFromPoints(wallModel.startPoint, wallModel.endPoint));
      (wallModel.passages || (wallModel.passages = [])).push(passageModel);
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.PASSAGE_ADDED);
      fetchAttributesForObject(passageModel, ObjectType.Passage, regionCode).then((attrs) => {
        const widthCm = passageModel.length * INTERNAL_TO_CM_FACTOR;
        const widthValue =
          unitType === EUnitType.METRIC
            ? parseFloat(widthCm.toFixed(3))
            : Math.round(convertTo(widthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
        const widthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.PASSAGE_WIDTH);
        if (widthAttr) {
          widthAttr.value = widthValue;
          widthAttr.unit = unit;
        }
        updatePassageAttributes(index, wallModel.passages.length - 1, attrs);
      });
    } else if (wallMode === EWallMode.RADIATOR) {
      const distA = distanceToWallNormal(e.point, wallModel.outerStartPoint2, wallModel.outerEndPoint2);
      const distB = distanceToWallNormal(e.point, wallModel.outerStartPoint, wallModel.outerEndPoint);
      const side: Side = distA <= distB ? 'A' : 'B';
      const radiatorPosition = snapRadiatorToWall(wallModel, e.point, DEFAULT_RADIATOR_LENGTH, DEFAULT_RADIATOR_DEPTH, 0, side);
      const radiatorAngle = getAngleFromPoints(wallModel.startPoint, wallModel.endPoint);
      const radiatorModel = new RadiatorModel(radiatorPosition, DEFAULT_RADIATOR_LENGTH, DEFAULT_RADIATOR_DEPTH, radiatorAngle);

      if (!wallModel.sides[side]) wallModel.sides[side as Side] = { radiators: [] };
      const newRadiatorIndex = wallModel.sides[side].radiators.length;
      wallModel.sides[side].radiators = [...wallModel.sides[side].radiators, radiatorModel];
      onWallUpdate(wallModel.uuid, wallModel, EHistoryAction.RADIATOR_ADDED);

      fetchAttributesForObject(radiatorModel, ObjectType.Radiator, regionCode).then((attrs) => {
        const lengthCm = radiatorModel.length * INTERNAL_TO_CM_FACTOR;
        const depthCm = radiatorModel.depth * INTERNAL_TO_CM_FACTOR;
        const lengthValue =
          unitType === EUnitType.METRIC
            ? parseFloat(lengthCm.toFixed(3))
            : Math.round(convertTo(lengthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
        const depthValue =
          unitType === EUnitType.METRIC
            ? parseFloat(depthCm.toFixed(3))
            : Math.round(convertTo(depthCm, 'cm' as UnitsByMeasure<MeasureKind.Length>, 'inches' as UnitsByMeasure<MeasureKind.Length>));
        const lengthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.RADIATOR_LENGTH);
        if (lengthAttr) {
          lengthAttr.value = lengthValue;
          lengthAttr.unit = unit;
        }
        const depthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.RADIATOR_DEPTH);
        if (depthAttr) {
          depthAttr.value = depthValue;
          depthAttr.unit = unit;
        }
        updateRadiatorAttributes(index, side, newRadiatorIndex, attrs);
      });
    } else if (onClick) {
      setCurrentlyActiveSubMenu(ESubMenu.NONE);
      if (!isDragging && !wallPerpendicularDrag && interactionMode !== EInteractionMode.MOVE) {
        onClick(e);
        triggerLongPressManually();
      }
    }
  };

  useEffect(() => {
    if (
      !wallModel ||
      ((wallModel.windows?.length ?? 0) === 0 &&
        (wallModel.doors?.length ?? 0) === 0 &&
        (wallModel.passages?.length ?? 0) === 0 &&
        (wallModel.sides?.A?.radiators?.length ?? 0) === 0 &&
        (wallModel.sides?.B?.radiators?.length ?? 0) === 0)
    ) {
      return;
    }

    let elementsChanged = false;
    const angle = getAngleFromPoints(wallModel.startPoint, wallModel.endPoint);

    // --- WINDOWS ---
    wallModel.windows = (wallModel.windows || []).map((window) => {
      const originalPos = (window.position as any)?.clone ? (window.position as THREE.Vector3).clone() : new THREE.Vector3(...(window.position as any));

      const snappedPos = snapWindowToWall(wallModel, originalPos.clone());

      if (originalPos.distanceTo(snappedPos) > 0.0001 || window.depth !== wallModel.depth || window.angle !== angle) {
        window.position = snappedPos;
        window.angle = angle;
        window.depth = wallModel.depth;
        elementsChanged = true;
      }
      return window;
    });

    // --- DOORS ---
    wallModel.doors = (wallModel.doors || []).map((door) => {
      const originalPos = (door.position as any)?.clone ? (door.position as THREE.Vector3).clone() : new THREE.Vector3(...(door.position as any));

      const snappedPos = snapDoorToWall(wallModel, originalPos.clone());

      if (originalPos.distanceTo(snappedPos) > 0.0001 || door.depth !== wallModel.depth || door.angle !== angle) {
        door.position = snappedPos;
        door.angle = angle;
        door.depth = wallModel.depth;
        elementsChanged = true;
      }
      return door;
    });

    // --- PASSAGES ---
    wallModel.passages = (wallModel.passages || []).map((passage) => {
      const originalPos = (passage.position as any)?.clone ? (passage.position as THREE.Vector3).clone() : new THREE.Vector3(...(passage.position as any));

      const snappedPos = snapPassageToWall(wallModel, originalPos.clone());

      if (originalPos.distanceTo(snappedPos) > 0.0001 || passage.depth !== wallModel.depth || passage.angle !== angle) {
        passage.position = snappedPos;
        passage.angle = angle;
        passage.depth = wallModel.depth;
        elementsChanged = true;
      }
      return passage;
    });

    // --- RADIATORS ---
    const updateRadiatorsForSide = (side: 'A' | 'B') => {
      if (!wallModel.sides?.[side]) return;

      wallModel.sides[side].radiators = (wallModel.sides[side].radiators || []).map((radiator) => {
        const originalPos = (radiator.position as any)?.clone ? (radiator.position as THREE.Vector3).clone() : new THREE.Vector3(...(radiator.position as any));

        const snappedPos = snapRadiatorToWall(wallModel, originalPos.clone(), radiator.length, radiator.depth, radiator.nicheDepth ?? 0, side);

        if (originalPos.distanceTo(snappedPos) < 0.0001 && radiator.angle === angle) {
          return radiator;
        }

        const updated = new RadiatorModel(snappedPos, radiator.length, radiator.depth, angle, radiator.attributes, undefined, radiator.nicheDepth ?? 0);
        updated.setUUID(radiator.uuid);
        elementsChanged = true;
        return updated;
      });
    };

    updateRadiatorsForSide('A');
    updateRadiatorsForSide('B');

    // Sync to building context only when drag has finished
    if (!isDragging && elementsChanged) {
      onWallUpdate(wallModel.uuid, wallModel);
    }
  }, [wallModel?.uuid, wallModel?.depth, wallModel?.startPoint?.x, wallModel?.startPoint?.y, wallModel?.endPoint?.x, wallModel?.endPoint?.y, isDragging, onWallUpdate]);

  useEffect(() => {
    if (activeDrawer === EActiveDrawer.WALL && selectedWall.wallIndex === index) {
      setOverlayVisibility(true);
    } else {
      setOverlayVisibility(false);
    }
  }, [activeDrawer, index, selectedWall.wallIndex]);

  const geometry = useMemo(() => {
    const geom = new THREE.ShapeGeometry(shape);
    geom.rotateX(Math.PI / 2);
    return geom;
  }, [shape]);

  const wallLengthArrowIcon = useMemo(() => {
    switch (wallChangeDirection) {
      default:
      case EDirection.BOTH:
        return IconDoubleArrow;
      case EDirection.LEFT:
        return IconSingleArrowRight;
      case EDirection.RIGHT:
        return IconSingleArrowLeft;
    }
  }, [wallChangeDirection]);

  const [tempPoint, setTempPoint] = useState<THREE.Vector3 | null>(null);

  const handleDoorClickWrapper = (wallIndex: number, doorIndex: number) => {
    onDoorClick(wallIndex, doorIndex);
  };

  const handlePassageClickWrapper = (wallIndex: number, passageIndex: number) => {
    onPassageClick(wallIndex, passageIndex);
  };

  const wallIsShared = isWallShared(building, wallModel.uuid).shared;
  const wallAlreadyRenderedBySpaceIndicators = useMemo(() => {
    return (building.rooms ?? []).some((room: any) =>
      (room.spaces ?? []).some((space: any) =>
        (space.boundaries ?? []).some((boundary: any) => {
          if (!(boundary instanceof PhysicalBoundaryModel)) return false;
          if (!Array.isArray(boundary.edges) || boundary.edges.length < 3) return false;

          return boundary.edges.some((edge: any) => edge?.wall?.uuid === wallModel.uuid);
        }),
      ),
    );
  }, [building.rooms, wallModel.uuid]);
  const outlineColor = selectedWall.wallIndex === index ? '#009fe3' : hovered ? '#565656' : '#222222';
  const outlineWidth = 6;
  const meshZ = 0.02 + index * 0.0001;
  const lineZ = 0.01 + index * 0.0001;

  const lineSideB = useMemo(() => [new THREE.Vector3(pointC.x, pointC.y, lineZ), new THREE.Vector3(pointD.x, pointD.y, lineZ)], [pointC, pointD]);
  const lineSideA = useMemo(() => [new THREE.Vector3(pointC2.x, pointC2.y, lineZ), new THREE.Vector3(pointD2.x, pointD2.y, lineZ)], [pointC2, pointD2]);
  const lineCapStart = useMemo(() => [new THREE.Vector3(pointC.x, pointC.y, lineZ), new THREE.Vector3(pointC2.x, pointC2.y, lineZ)], [pointC, pointC2]);
  const lineCapEnd = useMemo(() => [new THREE.Vector3(pointD.x, pointD.y, lineZ), new THREE.Vector3(pointD2.x, pointD2.y, lineZ)], [pointD, pointD2]);

  const hasStartNeighbor = useMemo(() => {
    return building.walls.some((w, i) => i !== index && (w.startPoint.distanceTo(wallModel.startPoint) < 0.1 || w.endPoint.distanceTo(wallModel.startPoint) < 0.1));
  }, [building.walls, wallModel.startPoint, index]);

  const hasEndNeighbor = useMemo(() => {
    return building.walls.some((w, i) => i !== index && (w.startPoint.distanceTo(wallModel.endPoint) < 0.1 || w.endPoint.distanceTo(wallModel.endPoint) < 0.1));
  }, [building.walls, wallModel.endPoint, index]);

  // We use the already calculated pointC2 and pointD2 (Side A / Inner Edge)
  // to ensure the label matches the visual boundary of the room.
  const innerDisplayLength = useMemo(() => {
    if (pointC2 && pointD2) {
      return pointC2.distanceTo(pointD2) / UNIT_TO_METER_DIVISOR;
    }
    return wallModel.sideALength;
  }, [pointC2, pointD2, wallModel.sideALength]);

  return (
    <group name={`group-standardWall#${index}`} renderOrder={RENDER_ORDERS.wall}>
      {overlayVisibility && <WallLengthArrowOverlay wallModel={wallModel} icon={wallLengthArrowIcon} />}

      {/* MAIN WALL MESH */}
      <mesh
        name={'standardWall-mesh'}
        geometry={geometry}
        position={[0, 0, meshZ]}
        rotation={[Math.PI / 2, 0, 0]}
        onPointerMove={(e) => {
          if (wallMode === EWallMode.RADIATOR) setTempPoint(e.point.clone());
        }}
        // WICHTIG: onPointerOver mit stopPropagation!
        onPointerOver={(e) => {
          e.stopPropagation(); // Verhindert, dass der Strahl durch die Wand auf den Boden durchschlägt
          if (!isDragging && !wallPerpendicularDrag) {
            setHovered(true);
          }
        }}
        // WICHTIG: onPointerOut statt Leave!
        onPointerOut={(e) => {
          setHovered(false);
          stopLongPressManually();
          setTempPoint(null);
        }}
        onPointerUp={() => stopLongPressManually()}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          if (onClick && !isDragging) handleWallMeshClick(event);
        }}
      >
        <primitive object={hatchMaterial} attach="material" />
      </mesh>

      {/* DIE NEUEN SMART OUTLINES */}
      {/* Innen- und Außenkante werden immer gezeichnet */}
      <Line points={lineSideB} color={outlineColor} lineWidth={outlineWidth} />
      <Line points={lineSideA} color={outlineColor} lineWidth={outlineWidth} />

      {!hasStartNeighbor && <Line points={lineCapStart} color={outlineColor} lineWidth={outlineWidth} />}
      {!hasEndNeighbor && <Line points={lineCapEnd} color={outlineColor} lineWidth={outlineWidth} />}
      {wallMode === EWallMode.RADIATOR && hovered && tempPoint && (
        <TempRadiator wall={wallModel} pointer={tempPoint} width={DEFAULT_RADIATOR_LENGTH} thickness={DEFAULT_RADIATOR_DEPTH} nicheDepth={0} />
      )}

      {(wallModel.windows || []).map((window, idx) => (
        <MemoizedWindow
          key={idx}
          window={window}
          wallModel={wallModel}
          onWallUpdate={onWallUpdate}
          onClick={() => onWindowClick(index, idx)}
          wallIndex={index}
          windowIndex={idx}
          selectedWindow={selectedWindow && selectedWindow.wallIndex === index && selectedWindow.windowIndex === idx ? selectedWindow : ({} as ISelectedWindow)}
        />
      ))}

      {(['A', 'B'] as const).map((side) =>
        (wallModel.sides?.[side]?.radiators || []).map((radiator, idx) => {
          const isSelected = selectedRadiator && selectedRadiator.wallIndex === index && selectedRadiator.radiatorIndex === idx && selectedRadiator.side === side;
          return (
            <MemoizedRadiator
              key={`rad-${side}-${idx}`}
              radiator={radiator}
              wallModel={wallModel}
              side={side}
              radiatorIndex={idx}
              wallIndex={index}
              onClick={() => onRadiatorClick(index, idx, side)}
              onWallUpdate={onWallUpdate}
              selected={isSelected}
            />
          );
        }),
      )}

      {(wallModel.doors || []).map((door, idx) => (
        <MemoizedDoor
          key={idx}
          door={door}
          wallModel={wallModel}
          wallIndex={index}
          doorIndex={idx}
          onWallUpdate={onWallUpdate}
          onClick={() => handleDoorClickWrapper(index, idx)}
          selectedDoor={selectedDoor && selectedDoor.wallIndex === index && selectedDoor.doorIndex === idx ? selectedDoor : ({} as ISelectedDoor)}
        />
      ))}

      {(wallModel.passages || []).map((passage, idx) => (
        <MemoizedPassage
          key={idx}
          passage={passage}
          wallModel={wallModel}
          wallIndex={index}
          passageIndex={idx}
          onWallUpdate={onWallUpdate}
          onClick={() => handlePassageClickWrapper(index, idx)}
          selectedPassage={selectedPassage && selectedPassage.wallIndex === index && selectedPassage.passageIndex === idx ? selectedPassage : ({} as ISelectedPassage)}
        />
      ))}

      {isPreview && (showWallInnerLength || showWallOuterLength) && !wallAlreadyRenderedBySpaceIndicators && (
        <WallLengthIndicator
          startPoint={new THREE.Vector3(pointC2.x, pointC2.y, 0)}
          endPoint={new THREE.Vector3(pointD2.x, pointD2.y, 0)}
          outerStartPoint={wallModel.outerStartPoint}
          outerEndPoint={wallModel.outerEndPoint}
          wallSideALength={innerDisplayLength}
          wallSideBLength={wallModel.sideBLength}
          shiftIndicator={showElementDistances && hasWindowsDoorsOrPassages}
        />
      )}
      {showWallOuterLength && !wallIsShared && (
        <OuterWallLengthIndicator
          startPoint={wallModel.startPoint.clone()}
          endPoint={wallModel.endPoint.clone()}
          outerStartPoint={wallModel.outerStartPoint}
          outerEndPoint={wallModel.outerEndPoint}
          wallSideBLength={wallModel.sideBLength}
          shiftIndicator={showElementDistances && hasWindowsDoorsOrPassages}
          isWallSelected={selectedWall.wallIndex === index}
        />
      )}

      {!isPreview && <DraggablePoint position={wallModel.startPoint.clone()} pointType={EDraggablePointType.START} onStartDragging={setDraggingPosition} allowPropagation={true} />}
      {/* Draggable ending point of the wall */}
      {!isPreview && <DraggablePoint position={wallModel.endPoint.clone()} pointType={EDraggablePointType.END} onStartDragging={setDraggingPosition} allowPropagation={true} />}
    </group>
  );
};
export default StandardWall;
